"use client";

import { Check, Copy } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";

/** Puts `text` on the clipboard. Falls back to a hidden text box where the browser has no clipboard access (an address that is not secure, older browsers). */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const box = document.createElement("textarea");
    box.value = text;
    box.style.position = "fixed";
    box.style.opacity = "0";
    document.body.appendChild(box);
    box.select();
    const ok = document.execCommand("copy");
    box.remove();
    return ok;
  }
}

/** The bar on top of a code block (inside its frame, which the caller draws) on the public page: the language (when set) and a Copy button. It copies the words the reader has selected inside this block, or the whole code when nothing is selected; the label then says "Copied" (or that it failed) for two seconds. */
export function CopyCodeButton({ code, language }: { code: string; language?: string }) {
  const t = useTranslations("publicBlog");
  const [state, setState] = useState<"idle" | "done" | "failed">("idle");
  const bar = useRef<HTMLDivElement>(null);
  async function copy() {
    const picked = window.getSelection()?.toString() ?? "";
    const inside = picked !== "" && bar.current?.parentElement?.contains(window.getSelection()?.anchorNode ?? null);
    setState((await copyText(inside ? picked : code)) ? "done" : "failed");
    setTimeout(() => setState("idle"), 2000);
  }
  const label = t(state === "done" ? "copiedCode" : state === "failed" ? "copyFailedCode" : "copyCode");
  return (
    <div ref={bar} className="flex items-center justify-between gap-2 border-b bg-background/40 px-3 py-1">
      <span className="font-mono text-xs uppercase text-muted-foreground">{language}</span>
      <Button type="button" variant="ghost" size="sm" onMouseDown={(e) => e.preventDefault()} onClick={copy}>
        {state === "done" ? <Check aria-hidden /> : <Copy aria-hidden />}<span aria-live="polite">{label}</span>
      </Button>
    </div>
  );
}
